import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { navigate } from '../app/router';
import { Icon, type IconName } from '../components/ui/Icon';
import { Sheet, Switch, confirmDialog } from '../components/ui/ui';
import { BUILT_IN_DESIGNS } from '../designs/catalog';
import { FONTS } from '../designs/fonts';
import { formatBytes, formatNumber } from '../lib/format';
import { InstallHelp } from '../lockscreen/PreviewScreen';
import { charCount } from '../notes/model';
import { TEMPLATES } from '../notes/templates';
import { NotificationPanel } from '../scheduling/SchedulesScreen';
import { store, useStore } from '../state/store';
import { downloadBlob, exportBackup, importBackup, notesToMarkdown } from '../storage/backup';
import { collectGarbageAssets, pruneSnapshots, requestPersistence, storageEstimate } from '../storage/repo';
import type { CompletedBehavior } from '../storage/schemas';
import './settings.css';

const stamp = () => new Date().toISOString().slice(0, 10);

/** Reference-style row: icon, bold label, optional description, control or chevron. */
function SRow({ icon, label, detail, children, onClick, danger }: { icon?: IconName; label: ReactNode; detail?: ReactNode; children?: ReactNode; onClick?: () => void; danger?: boolean }) {
  const body = (
    <>
      <span className="sr-icon">{icon && <Icon name={icon} size={26} />}</span>
      <span className="sr-text">
        <span className="sr-label">{label}</span>
        {detail && <span className="sr-detail">{detail}</span>}
      </span>
      {children && <span className="sr-control">{children}</span>}
      {onClick && !children && <Icon name="forward" size={22} className="sr-chev" />}
    </>
  );
  return onClick ? (
    <button type="button" className={`srow is-button ${danger ? 'is-danger' : ''}`} onClick={onClick}>
      {body}
    </button>
  ) : (
    <div className={`srow ${danger ? 'is-danger' : ''}`}>{body}</div>
  );
}

function SGroup({ title, children, footer }: { title: string; children: ReactNode; footer?: ReactNode }) {
  return (
    <section className="sgroup">
      <h3>{title}</h3>
      {children}
      {footer && <p className="sgroup-foot">{footer}</p>}
    </section>
  );
}

const FAQ: [string, string][] = [
  ['Is MemoNest free?', 'Yes. There is no account, no subscription and no advertising.'],
  ['Can MemoNest put a note on my real lock screen?', 'Not directly — web apps cannot add lock-screen widgets. Use “Lock-screen preview → Save lock-screen wallpaper” and set the image as your wallpaper.'],
  ['Where are my notes stored?', 'Only in this browser on this device (IndexedDB). Nothing is uploaded. Make a backup if you change phones.'],
  ['Why did my reminder not appear?', 'Browsers only run web apps while they are open or recently used. On iPhone, add MemoNest to the Home Screen and allow notifications. Reminders you missed are shown when you return.'],
  ['How do I get my previous notes back?', 'Open Settings → History for older pages, or a note’s Version history for earlier versions of it.'],
];

export function SettingsPanel({ onClose, onNoteOptions, onNewPage }: { onClose?: () => void; onNoteOptions?: () => void; onNewPage?: () => void }) {
  const settings = useStore((s) => s.settings);
  const notes = useStore((s) => s.notes);
  const meta = useStore((s) => s.meta);
  const activeId = useStore((s) => s.active.noteId);
  const designs = useStore((s) => s.designs);
  const quarantine = useStore((s) => s.quarantine);
  const [est, setEst] = useState<Awaited<ReturnType<typeof storageEstimate>>>(null);
  const [sheet, setSheet] = useState<'none' | 'faq' | 'licenses' | 'privacy' | 'install'>('none');
  const fileRef = useRef<HTMLInputElement>(null);
  const set = (patch: Parameters<typeof store.updateSettings>[0]) => store.updateSettings(patch);
  const active = activeId ? notes[activeId] : undefined;

  useEffect(() => {
    void storageEstimate().then(setEst);
  }, []);

  const stats = useMemo(() => {
    const live = Object.values(notes).filter((n) => !n.deletedAt);
    return {
      characters: live.reduce((s, n) => s + charCount(n), 0),
      // Reference: "Pages" counts earlier pages kept in history, not the one on screen.
      pages: live.filter((n) => n.id !== activeId).length,
      days: Math.max(1, Math.floor((Date.now() - meta.firstUseAt) / 864e5) + 1),
      since: new Date(meta.firstUseAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }),
    };
  }, [notes, activeId, meta.firstUseAt]);

  const go = (r: Parameters<typeof navigate>[0]) => {
    onClose?.();
    navigate(r);
  };

  const setOnNote = (patch: { compact?: boolean; watermark?: boolean }) => {
    if (active) store.updateNote(active.id, (n) => ({ ...n, style: { ...n.style, ...patch } }));
  };

  const backup = async (includeHistory: boolean) => {
    await store.flush();
    const b = await exportBackup({ includeHistory });
    downloadBlob(new Blob([JSON.stringify(b)], { type: 'application/json' }), `memonest-backup-${stamp()}.json`);
    store.toast('Backup downloaded. It is not encrypted — keep it somewhere safe.', 'success');
  };

  const supportInfo = () =>
    [`MemoNest ${__APP_VERSION__}`, `Browser: ${navigator.userAgent}`, `Screen: ${screen.width}×${screen.height} @${devicePixelRatio}x`, `Installed: ${matchMedia('(display-mode: standalone)').matches}`, `Notes: ${Object.keys(notes).length}`, `Notification permission: ${'Notification' in window ? Notification.permission : 'unsupported'}`].join('\n');

  return (
    <div className="settings-panel">
      <button type="button" className="sp-stats" onClick={() => go({ name: 'history' })} aria-label={`Open history. ${stats.characters} characters, ${stats.pages} pages, ${stats.days} days`}>
        <span>
          <small>Total Characters</small>
          <b>{formatNumber(stats.characters)}</b>
        </span>
        <span>
          <small>Pages</small>
          <b>{formatNumber(stats.pages)}</b>
        </span>
        <span>
          <small>Usage Days</small>
          <b>{formatNumber(stats.days)}</b>
          <em>{stats.since}~</em>
        </span>
      </button>

      <SGroup title="Customize">
        <SRow icon="compact" label="Compact notes">
          <Switch
            label="Compact notes"
            checked={active?.style.compact ?? settings.compactDefault}
            onChange={(v) => {
              set({ compactDefault: v });
              setOnNote({ compact: v });
            }}
          />
        </SRow>
        <SRow icon="logo" label="Show logo on note">
          <Switch
            label="Show logo on note"
            checked={active?.style.watermark ?? settings.watermark}
            onChange={(v) => {
              set({ watermark: v });
              setOnNote({ watermark: v });
            }}
          />
        </SRow>
        <SRow icon="template" label="Note Template" detail="What a new page starts with">
          <select aria-label="Note template" value={settings.newPageTemplate} onChange={(e) => set({ newPageTemplate: e.target.value })}>
            <option value="blank">Blank page</option>
            <option value="checklist">Empty checklist</option>
            {TEMPLATES.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </SRow>
        <SRow icon="emptynote" label="Show empty notes" detail="Keep an empty note visible in the lock-screen preview and wallpaper, so there is always a place to jot something down.">
          <Switch label="Show empty notes" checked={settings.showEmptyNotes} onChange={(v) => set({ showEmptyNotes: v })} />
        </SRow>
        <SRow icon="delline" label="Delete line when checked" detail="A checked line disappears one second after you tick it. The note is saved to its history first, so nothing is lost.">
          <Switch label="Delete line when checked" checked={settings.deleteCheckedLines} onChange={(v) => set({ deleteCheckedLines: v })} />
        </SRow>
      </SGroup>

      <SGroup title="MemoNest">
        {onNewPage && <SRow icon="plus" label="New page" detail="The current note stays in History" onClick={() => onNewPage()} />}
        {onNoteOptions && <SRow icon="more" label="This note" detail="Name, tags, pin, export, print, archive" onClick={() => onNoteOptions()} />}
        <SRow icon="history" label="History" detail="Earlier pages, trash and search" onClick={() => go({ name: 'history' })} />
        <SRow icon="eye" label="Lock-screen preview" detail="Simulated preview and wallpaper export" onClick={() => go({ name: 'preview' })} />
        <SRow icon="alarm" label="Schedules & reminders" onClick={() => go({ name: 'schedules' })} />
        <SRow icon="timeline" label="Upcoming" onClick={() => go({ name: 'upcoming' })} />
        <SRow icon="sparkles" label="Templates" onClick={() => go({ name: 'templates' })} />
        {active && <SRow icon="copy" label="Version history of this note" onClick={() => go({ name: 'versions', id: active.id })} />}
      </SGroup>

      <div className="sp-embedded">
        <NotificationPanel />
      </div>

      <SGroup title="Quiet hours" footer="Reminders in quiet hours are shown when they end. Scheduled note switches still happen.">
        <SRow icon="bell" label="Quiet hours">
          <Switch label="Quiet hours" checked={settings.quietHours.enabled} onChange={(v) => set({ quietHours: { ...settings.quietHours, enabled: v } })} />
        </SRow>
        {settings.quietHours.enabled && (
          <SRow label="From – until">
            <input type="time" aria-label="Quiet hours start" value={settings.quietHours.start} onChange={(e) => e.target.value && set({ quietHours: { ...settings.quietHours, start: e.target.value } })} />
            <input type="time" aria-label="Quiet hours end" value={settings.quietHours.end} onChange={(e) => e.target.value && set({ quietHours: { ...settings.quietHours, end: e.target.value } })} />
          </SRow>
        )}
      </SGroup>

      <SGroup title="New notes">
        <SRow label="Default design">
          <select aria-label="Default design" value={settings.defaultDesignId} onChange={(e) => set({ defaultDesignId: e.target.value })}>
            {[...designs, ...BUILT_IN_DESIGNS].map((d) => (
              <option key={d.id} value={d.id}>
                {d.category} · {d.name}
              </option>
            ))}
          </select>
        </SRow>
        <SRow label="Shrink text to fit">
          <Switch label="Shrink text to fit" checked={settings.autoSize} onChange={(v) => set({ autoSize: v })} />
        </SRow>
        <SRow label="Completed items">
          <select aria-label="Completed items" value={settings.completedBehavior} onChange={(e) => set({ completedBehavior: e.target.value as CompletedBehavior })}>
            <option value="strike">Cross out</option>
            <option value="bottom">Move to bottom</option>
            <option value="hide">Hide</option>
          </select>
        </SRow>
      </SGroup>

      <SGroup title="Version history" footer="Versions are saved automatically while you edit, and before every restore or delete.">
        <SRow label="Save a version every">
          <select aria-label="Auto-save interval" value={settings.history.autoSnapshotMinutes} onChange={(e) => set({ history: { ...settings.history, autoSnapshotMinutes: Number(e.target.value) } })}>
            {[2, 5, 10, 30, 60, 240].map((m) => (
              <option key={m} value={m}>
                {m < 60 ? `${m} min` : `${m / 60} h`}
              </option>
            ))}
          </select>
        </SRow>
        <SRow label="Keep per note">
          <select aria-label="Versions kept per note" value={settings.history.maxPerNote} onChange={(e) => set({ history: { ...settings.history, maxPerNote: Number(e.target.value) } })}>
            {[10, 25, 50, 100, 250].map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </SRow>
        <SRow label="Delete versions older than">
          <select aria-label="Version age limit" value={settings.history.maxAgeDays} onChange={(e) => set({ history: { ...settings.history, maxAgeDays: Number(e.target.value) } })}>
            {[30, 90, 180, 365, 3650].map((m) => (
              <option key={m} value={m}>
                {m >= 3650 ? 'Never' : `${m} days`}
              </option>
            ))}
          </select>
        </SRow>
        <SRow
          label="Apply now"
          onClick={async () => {
            const n = await pruneSnapshots(settings.history.maxPerNote, settings.history.maxAgeDays);
            store.toast(`Removed ${n} old version${n === 1 ? '' : 's'}.`);
          }}
        />
      </SGroup>

      <SGroup title="Backup & data" footer="Backups are plain JSON files with your images inside. They are not encrypted.">
        <SRow icon="download" label="Back up everything" onClick={() => void backup(true)} />
        <SRow icon="download" label="Back up without history" onClick={() => void backup(false)} />
        <SRow
          icon="share"
          label="Export all as Markdown"
          onClick={() => downloadBlob(new Blob([notesToMarkdown(Object.values(notes).filter((n) => !n.deletedAt))], { type: 'text/markdown' }), `memonest-notes-${stamp()}.md`)}
        />
        <SRow icon="upload" label="Restore from backup…" detail="Merges with this device; newer copies win" onClick={() => fileRef.current?.click()} />
        <input
          ref={fileRef}
          type="file"
          accept="application/json,.json"
          hidden
          onChange={async (e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (!f) return;
            try {
              const r = await importBackup(await f.text());
              await store.reloadAll();
              store.toast(`Imported ${r.notes} notes, ${r.schedules} schedules, ${r.designs} designs${r.skipped.length ? ` (${r.skipped.length} skipped)` : ''}.`, 'success');
            } catch (err) {
              store.toast((err as Error).message, 'error');
            }
          }}
        />
      </SGroup>

      <SGroup
        title="Storage"
        footer="Notes live only in this browser on this device. Clearing site data, removing the Home Screen app on iPhone, or private browsing deletes them. Safari may clear sites that are not on the Home Screen after 7 days without use."
      >
        <SRow label="Used" detail={est ? `${formatBytes(est.usage)} of ${formatBytes(est.quota)}` : 'Unknown'} />
        <SRow label="Protected from automatic cleanup" detail={est?.persisted ? 'Yes' : 'Not yet'}>
          {!est?.persisted && (
            <button
              type="button"
              className="sp-btn"
              onClick={async () => {
                const ok = await requestPersistence();
                setEst(await storageEstimate());
                store.toast(ok ? 'Storage is now protected.' : 'The browser declined. Installing MemoNest usually helps.');
              }}
            >
              Request
            </button>
          )}
        </SRow>
        <SRow
          label="Remove unused images"
          onClick={async () => {
            const n = await collectGarbageAssets(store.referencedAssets());
            store.toast(`Removed ${n} unused file${n === 1 ? '' : 's'}.`);
            setEst(await storageEstimate());
          }}
        />
        {quarantine.length > 0 && <SRow label="Damaged records skipped" detail={`${quarantine.length} record(s) could not be read and were left untouched.`} />}
      </SGroup>

      <SGroup title="Help">
        <SRow
          icon="replay"
          label="See tutorial again"
          onClick={() => {
            set({ tutorialSeen: false });
            onClose?.();
            navigate({ name: 'home' });
          }}
        />
        <SRow icon="help" label="FAQ" onClick={() => setSheet('faq')} />
        <SRow icon="phone" label="Install on your phone" onClick={() => setSheet('install')} />
        <SRow
          icon="copyinfo"
          label="Copy Support Info"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(supportInfo());
              store.toast('Support info copied.', 'success');
            } catch {
              store.toast('Could not access the clipboard.', 'error');
            }
          }}
        />
        <SRow icon="info" label="Keyboard shortcuts" detail="⌘/Ctrl+Z undo · ⌘/Ctrl+Shift+Z redo · ⌘/Ctrl+S save · ⌘/Ctrl+Shift+L checklist · Alt+↑/↓ move line · Esc close" />
      </SGroup>

      <SGroup title="Feedback">
        <SRow
          icon="share"
          label="Share with friends"
          onClick={async () => {
            const data = { title: 'MemoNest', text: 'Notes styled for your lock screen.', url: location.origin };
            try {
              if (navigator.share) await navigator.share(data);
              else {
                await navigator.clipboard.writeText(data.url);
                store.toast('Link copied.', 'success');
              }
            } catch {
              /* cancelled */
            }
          }}
        />
      </SGroup>

      <SGroup title="Legal Information">
        <SRow icon="doc" label="Privacy" onClick={() => setSheet('privacy')} />
        <SRow icon="doc" label="Licenses" onClick={() => setSheet('licenses')} />
      </SGroup>

      <SGroup title="Danger zone">
        <SRow
          icon="trash"
          danger
          label="Delete all data on this device"
          onClick={async () => {
            if (await confirmDialog({ title: 'Delete everything?', message: 'All notes, images, designs, schedules and history on this device are erased. Back up first if you want to keep them.', confirmLabel: 'Delete everything', danger: true })) {
              await store.wipeEverything();
              onClose?.();
              navigate({ name: 'home' }, true);
            }
          }}
        />
      </SGroup>

      <footer className="sp-foot">
        <span className="sp-mark" aria-hidden="true">
          Memo
          <br />
          Nest
        </span>
        <p>Write it down, keep it in sight.<br />Everything stays on your device.</p>
        <small>MemoNest {__APP_VERSION__}</small>
      </footer>

      <Sheet open={sheet === 'faq'} onClose={() => setSheet('none')} title="FAQ" tall>
        <dl className="help-pane faq">
          {FAQ.map(([q, a]) => (
            <div key={q}>
              <dt>{q}</dt>
              <dd>{a}</dd>
            </div>
          ))}
        </dl>
      </Sheet>
      <Sheet open={sheet === 'privacy'} onClose={() => setSheet('none')} title="Privacy" tall>
        <div className="help-pane">
          <p>MemoNest has no account, no analytics, no advertising and no server. Your notes, images, drawings, designs, schedules and history are stored in this browser’s local database on this device.</p>
          <p>Nothing leaves the device unless you export a backup, save an image, share or print. Notification permission is only requested when you tap “Enable”. Backups are not encrypted.</p>
        </div>
      </Sheet>
      <Sheet open={sheet === 'licenses'} onClose={() => setSheet('none')} title="Licenses" tall>
        <div className="help-pane">
          <p>Fonts are bundled from Google Fonts via Fontsource under the SIL Open Font License 1.1 or Apache License 2.0:</p>
          <p className="lic-fonts">{FONTS.map((f) => f.label).join(' · ')}</p>
          <p>Software: React, idb, zod, Workbox (MIT). Icons, illustrations, decorations, wallpapers and the app icon are original MemoNest artwork.</p>
          <p>Design colour palettes in the first gallery sections follow the original LockScreenNotes app for compatibility of look; MemoNest is an independent project.</p>
        </div>
      </Sheet>
      <InstallHelp open={sheet === 'install'} onClose={() => setSheet('none')} />
    </div>
  );
}
